/*
 * <rs-hero-slider>: behaviour for sections/rs-hero-slider.liquid.
 *  - slide or fade transition, looping, dots, pause button, keyboard and touch swipe
 *  - autoplay that pauses on hover (optional), keyboard focus, hidden tab, off-screen and reduced motion
 *  - measures the announcement bar + header so full-screen / fit-to-image height never overshoots the viewport
 *  - safe in the Shopify theme editor: re-initialises on section load, cleans up on unload,
 *    and jumps to a slide when its block is selected
 */
(() => {
  if (customElements.get('rs-hero-slider')) return;

  class RsHeroSlider extends HTMLElement {
    connectedCallback() {
      this.destroy();

      this.viewport = this.querySelector('.rs-hero__viewport');
      this.track = this.querySelector('.rs-hero__track');
      this.slides = Array.from(this.querySelectorAll('.rs-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.rs-hero__dot'));
      this.pauseButton = this.querySelector('.rs-hero__pause');
      if (!this.track || this.slides.length === 0) return;

      this.count = this.slides.length;
      this.index = 0;
      this.busy = false;
      this.shifted = null;
      this.mode = this.dataset.transition === 'fade' ? 'fade' : 'slide';
      this.interval = (Number(this.dataset.interval) || 6) * 1000;
      this.baseDuration = Number(this.dataset.duration) || 700;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.count > 1;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true' && window.matchMedia('(hover: hover)').matches;

      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;
      this.inView = true;

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.applyMotionPreference();

      this.bind();
      this.updateState();
      this.updateOffset();
      this.preloadNext();
      this.schedule();
    }

    disconnectedCallback() {
      this.destroy();
    }

    /* ---------- setup / teardown ---------- */

    bind() {
      this.listeners = [];
      const on = (target, type, handler, options) => {
        target.addEventListener(type, handler, options);
        this.listeners.push(() => target.removeEventListener(type, handler, options));
      };

      if (this.count > 1) {
        this.dots.forEach((dot, i) =>
          on(dot, 'click', () => this.goTo(i, i > this.index ? 1 : -1))
        );
        if (this.pauseButton) on(this.pauseButton, 'click', () => this.togglePause());

        on(this, 'keydown', (event) => this.onKeydown(event));

        on(this.viewport, 'pointerdown', (event) => this.onPointerDown(event));
        on(this.viewport, 'pointermove', (event) => this.onPointerMove(event));
        on(this.viewport, 'pointerup', (event) => this.onPointerUp(event));
        on(this.viewport, 'pointercancel', (event) => this.onPointerUp(event, true));
      }

      if (this.pauseOnHover) {
        on(this, 'mouseenter', () => {
          this.hovered = true;
          this.schedule();
        });
        on(this, 'mouseleave', () => {
          this.hovered = false;
          this.schedule();
        });
      }

      // Only keyboard focus pauses autoplay; a mouse click on an arrow should not freeze it.
      on(this, 'focusin', (event) => {
        this.focused = event.target.matches(':focus-visible');
        this.schedule();
      });
      on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.focused = false;
          this.schedule();
        }
      });

      on(document, 'visibilitychange', () => this.schedule());
      on(this.motionQuery, 'change', () => {
        this.applyMotionPreference();
        this.schedule();
      });

      on(window, 'resize', () => this.updateOffset());
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.updateOffset());

      // Theme editor: select a slide block to preview it, release to resume autoplay.
      on(document, 'shopify:block:select', (event) => {
        const slide = event.target.closest && event.target.closest('.rs-hero__slide');
        if (!slide || !this.contains(slide)) return;
        this.editorHold = true;
        this.goTo(this.slides.indexOf(slide), undefined, true);
      });
      on(document, 'shopify:block:deselect', (event) => {
        const slide = event.target.closest && event.target.closest('.rs-hero__slide');
        if (!slide || !this.contains(slide)) return;
        this.editorHold = false;
        this.schedule();
      });

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            this.schedule();
          },
          { threshold: 0.25 }
        );
        this.intersectionObserver.observe(this);
      }

      // Header height changes (fonts load, announcement text wraps): keep full-screen height exact.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateOffset());
        document
          .querySelectorAll('.shopify-section-group-header-group, .header-wrapper, .utility-bar')
          .forEach((element) => this.resizeObserver.observe(element));
      }
    }

    destroy() {
      clearTimeout(this.autoTimer);
      clearTimeout(this.transitionTimer);
      if (this.listeners) this.listeners.forEach((off) => off());
      this.listeners = null;
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.intersectionObserver = null;
      this.resizeObserver = null;
    }

    /* ---------- layout ---------- */

    // Full-screen height = viewport height minus everything above the hero (announcement bar + header).
    updateOffset() {
      if (!this.classList.contains('rs-hero--full') && !this.classList.contains('rs-hero--fit')) return;
      const top = this.getBoundingClientRect().top + window.scrollY;
      this.style.setProperty('--rs-hero-offset', `${Math.max(0, Math.round(top))}px`);
    }

    applyMotionPreference() {
      this.reduced = this.motionQuery.matches;
      this.style.setProperty('--rs-hero-duration', `${this.reduced ? 0 : this.baseDuration}ms`);
      if (this.pauseButton) this.pauseButton.hidden = this.reduced;
    }

    preloadNext() {
      const next = this.slides[(this.index + 1) % this.count];
      const image = next && next.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';
    }

    /* ---------- navigation ---------- */

    get duration() {
      return this.reduced ? 0 : this.baseDuration;
    }

    setTrackPosition(position, extraPx = 0) {
      this.track.style.transform = `translate3d(calc(${-position * 100}% + ${extraPx}px), 0, 0)`;
    }

    goTo(target, direction, instant = false) {
      if (this.count < 2) return;
      target = ((target % this.count) + this.count) % this.count;
      if (target === this.index) {
        this.schedule();
        return;
      }
      if (this.busy) this.finishTransition();

      const previous = this.index;
      const dir = direction || (target > previous ? 1 : -1);
      this.index = target;
      this.updateState();
      this.preloadNext();

      if (this.mode === 'fade') {
        this.schedule();
        return;
      }

      // Looping: slide the target in from the correct side instead of rewinding the whole track.
      let position = target;
      this.shifted = null;
      if (dir === 1 && target < previous) {
        this.shifted = this.slides[target];
        this.shifted.style.transform = `translateX(${this.count * 100}%)`;
        position = target + this.count;
      } else if (dir === -1 && target > previous) {
        this.shifted = this.slides[target];
        this.shifted.style.transform = `translateX(${-this.count * 100}%)`;
        position = target - this.count;
      }

      this.track.classList.remove('is-dragging');
      if (instant || this.duration === 0) {
        this.setTrackPosition(position);
        this.finishTransition();
      } else {
        this.busy = true;
        this.setTrackPosition(position);
        this.transitionTimer = setTimeout(() => this.finishTransition(), this.duration + 60);
      }
      this.schedule();
    }

    finishTransition() {
      clearTimeout(this.transitionTimer);
      if (this.shifted) {
        this.shifted.style.transform = '';
        this.shifted = null;
        this.track.classList.add('is-resetting');
        this.setTrackPosition(this.index);
        void this.track.offsetWidth; // apply without animating, then restore transitions
        this.track.classList.remove('is-resetting');
      } else if (this.mode === 'slide') {
        this.setTrackPosition(this.index);
      }
      this.busy = false;
    }

    updateState() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.inert = !active;
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1, -1);
      else if (event.key === 'ArrowRight') this.goTo(this.index + 1, 1);
    }

    /* ---------- touch / pen swipe ---------- */

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || event.target.closest('button')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, axis: null };
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;

      if (!drag.axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.axis === 'x') {
          if (this.busy) this.finishTransition();
          this.viewport.setPointerCapture(event.pointerId);
          if (this.mode === 'slide') this.track.classList.add('is-dragging');
        }
      }
      if (drag.axis !== 'x') return;

      drag.dx = dx;
      if (this.mode === 'slide') this.setTrackPosition(this.index, dx);
    }

    onPointerUp(event, cancelled = false) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      if (drag.axis !== 'x') return;

      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      this.track.classList.remove('is-dragging');

      // A swipe that started on a link must not also follow the link.
      const suppressClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      this.addEventListener('click', suppressClick, { capture: true, once: true });
      setTimeout(() => this.removeEventListener('click', suppressClick, true), 100);

      const threshold = Math.min(60, this.viewport.clientWidth * 0.12);
      if (!cancelled && Math.abs(drag.dx) > threshold) {
        const dir = drag.dx < 0 ? 1 : -1;
        this.goTo(this.index + dir, dir);
      } else if (this.mode === 'slide') {
        this.setTrackPosition(this.index);
      }
    }

    /* ---------- autoplay ---------- */

    get shouldPlay() {
      return (
        this.autoplayEnabled &&
        !this.userPaused &&
        !this.reduced &&
        !this.hovered &&
        !this.focused &&
        !this.editorHold &&
        this.inView &&
        !document.hidden
      );
    }

    schedule() {
      clearTimeout(this.autoTimer);
      const playing = this.shouldPlay;
      this.track.setAttribute('aria-live', playing ? 'off' : 'polite');
      if (playing) this.autoTimer = setTimeout(() => this.goTo(this.index + 1, 1), this.interval);
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      this.pauseButton.classList.toggle('is-paused', this.userPaused);
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
      );
      this.schedule();
    }
  }

  customElements.define('rs-hero-slider', RsHeroSlider);
})();
